import type * as Information from "./information";
import type * as User from "./user";
import type {
  ICaseClientEmailCompletionMarker,
  ICaseClientEmailSnapshot,
  ICaseWorkshopRef,
} from "./client-email";

export interface ICase {
  id: string;
  regNum: string;
  clientRegNum: string;
  vinNumber: string | null;
  callerName: string;
  status: EStatus;
  faultCode: string;
  policyType: string;
  images: {
    startCase: string[];
    finishCase: string[];
    // Customer photos copied from the originating case request (absent on older cases)
    caseRequest?: string[];
  };
  statusTimestamp: {
    createdAt: any;
    assignedAt: any;
    reassignedAt: any;
    acceptedAt: any;
    ongoingAt: any;
    finishedAt: any;
    closedAt: any;
    estimatedArrivalAt: any;
    etaReminderAcknowledgedAt?: any;
  };
  userData: {
    provider: ICaseUser;
    previousProvider: ICaseUser;
    client: ICaseUser;
    user: ICaseUser;
    creator: ICaseUser;
    customer: ICustomer;
  };
  choosedService: Information.EService[];
  location: {
    from: {
      coord: Information.ICoord;
      generatedAddress: string;
      address: string;
    };
    to: {
      coord: Information.ICoord;
      generatedAddress: string;
      address: string;
      // Set when the destination was picked from the client company's workshops
      workshop?: ICaseWorkshopRef | null;
    };
    distance: number;
    state: string[];
    city: string;
  };
  payment: IPayment;
  internalRemarks: IRemark[];
  externalRemarks: IRemark[];
  closeReason?: string;
  createdFrom?: Information.CaseCreatedFrom;
  etaReminderAcknowledgedBy?: string;
  liveTracking?: boolean;
  // Last client case email sent for this case; absent = never emailed
  clientEmail?: ICaseClientEmailSnapshot;
  // Completion email sent on finish; absent = not sent
  clientEmailCompletion?: ICaseClientEmailCompletionMarker;
}

export interface ICaseLog {
  createAt: any;
  creator: User.IUser;
  action: string;
  params: any;
}

export interface ICasePricing {
  id: string;
  payment: IPayment;
  servicePrice: Information.IStatePrice;
}

export interface IPayment {
  client: {
    pay: number;
    receive: number;
  };
  provider: {
    pay: number;
    receive: number;
  };
  customer: {
    payProvider: number;
    payRsa: number;
  };
  currency: string;
  tollFee: number;
}

export interface IRemark {
  id: string;
  createdAt?: any;
  creator: {
    name: string;
    id: string;
  };
  content: string;
}

export interface ICaseUser extends User.IUser {
  id: string;
}

export interface ICustomer {
  _uuid?: string;
  name: string;
  contactNum: string;
  email?: string;
  vehicle: User.IVehicle;
}

export const enum EStatus {
  CREATED = "created",
  ASSIGNED = "assigned",
  REASSIGNED = "reassigned",
  ACCEPTED = "accepted",
  ONGOING = "ongoing",
  FINISHED = "finished",
  CLOSED = "closed",
}

export interface ICloseReason {
  id: string;
  label: string;
  value: string;
  isActive: boolean;
  createdAt?: any;
  updatedAt?: any;
}

export interface IProviderService {
  id: string;
  label: string;
  value: string;
  isActive: boolean;
  createdAt?: any;
  updatedAt?: any;
}

export enum CloseReason {
  NOT_AVAILABLE = "Not available",
  NO_RESPOND = "No respond",
  MOBILIZED = "Mobilized",
  DUPLICATE_CASE = "Duplicate case",
  CHARGES_NOT_AGREED = "Charges not agreed",
  OTHER = "Other",
}

export enum LogType {
  RECORD = "record",
  USER = "user",
  REPORT = "report",
  OTHER = "other",
}

export enum TargetType {
  RECORD = "record",
  CASE_REQUEST = "case_request",
  USER = "user",
  IMAGE = "image",
  CUSTOMER = "customer",
  VEHICLE = "vehicle",
  REMARK = "remark",
  SFTP_POLICIES = "sftp_policies",
  SERVICE_PRICE = "service_price",
  COORDINATE = "coordinate",
  COMPANY = "company",
  LOCATION = "location",
  RECORD_PRICE = "record_price",
  POLICY = "policy",
  SERVICE = "service",
  STATE = "state",
  FAULT_CODE = "fault_code",
  OTHER = "other",
}

export type LogAction =
  | "create-case"
  | "start-case"
  | "accept-case"
  | "force-accept-case"
  | "finish-case"
  | "force-finish-case"
  | "close-case"
  | "force-close-case"
  | "assign-case"
  | "force-assign-case"
  | "reassign-case"
  | "add-remarks"
  | "force-update-case"
  | "send-client-email"
  | "upload-case-photo"
  | "delete-case-photo"
  | "create-user"
  | "update-user"
  | "delete-user"
  | "create-company"
  | "update-company"
  | "create-case-request"
  | "update-case-request"
  | "delete-case-request"
  | "accept-case-request"
  | "lock-case-request"
  | "unlock-case-request"
  | "update-case-journey"
  | "2fa-admin-reset"
  | "2fa-self-recovery"
  | "2fa-totp-enrolled"
  | "2fa-totp-removed"
  | "session-revoke-all"
  | "password-reset";
